import React, { useContext } from "react";
import { gql } from "apollo-boost";
import { useMutation } from "@apollo/react-hooks";
import { ModalContext } from "components/common";
import { STRIPE_SUBSCRIPTION_STATUSES } from "components/Pages/Boost/consts";
import { BOOST_DATA_QUERY } from "./query";

const UNDO_BOOST_SUBSCRIPTION_CANCEL = gql`
  mutation UndoCancelBoostSubscription(
    $input: UndoCancelBoostSubscriptionInput
  ) {
    undoCancelBoostSubscription(input: $input) {
      status
      code
      message
      display_message
    }
  }
`;

const CancelButton = ({ disabled, handleCancel }) => (
  <button
    className={`text-white self-end px-3 py-1 mt-4 ${
      disabled ? "bg-gray" : "bg-red-500 hover:bg-red-600"
    }`}
    type="button"
    onClick={handleCancel}
    disabled={disabled}
  >
    Cancel Subscription
  </button>
);

const RestoreButton = ({ disabled, loading, handleRestore }) => (
  <button
    className="text-white self-end px-3 py-1 mt-4 btn-green"
    type="button"
    onClick={handleRestore}
    disabled={disabled}
  >
    {loading ? "Restoring Subscription..." : "Restore Subscription"}
  </button>
);

const Detail = ({ scheduledCancellationDate }) => {
  if (scheduledCancellationDate) {
    return (
      <div>
        <span>Subscription set to cancel on</span>
        <span className="font-bold ml-2">{`${scheduledCancellationDate.toDateString()}`}</span>
      </div>
    );
  }
  return (
    <div>
      Subscription will renew each billing cycle. Cancel to set subscription to
      cancel at the end of the billing cycle. Past-due subscriptions cancel
      immediately.
    </div>
  );
};

/**
 * @param {Object} props
 * @param {string} props.email - (Required) The email associated with the subscription.
 * @param {Date} props.scheduledCancellationDate - (Required) The date the subscription will cancel.
 * @param {Object} props.scheduledUpdate - (Required) The date the subscription will cancel.
 * @param {number} props.scheduledUpdate.startDate - Start date of current schedule
 * @param {Date} props.scheduledResumeDate - (Required) The date the subscription will resume from being paused.
 * @param {number} props.subscriptionStatus - (Required) Status of the current subscription (not the product)
 */
const BoostSubscriptionCancel = (props) => {
  const {
    email,
    scheduledCancellationDate,
    scheduledUpdate,
    scheduledResumeDate,
    subscriptionStatus,
  } = props;
  const isDowngradeScheduled = !!scheduledUpdate?.startDate;
  const isCanceled = !!scheduledCancellationDate;
  const isPaused = !!scheduledResumeDate;
  const isPastDue =
    subscriptionStatus === STRIPE_SUBSCRIPTION_STATUSES.PAST_DUE;

  const { openModal } = useContext(ModalContext);

  const [undoCancelBoostSubscription, { loading, error }] = useMutation(
    UNDO_BOOST_SUBSCRIPTION_CANCEL,
    {
      variables: { input: { email } },
      refetchQueries: [{ query: BOOST_DATA_QUERY, variables: { email } }],
    },
  );

  const errorMessage = error?.graphQLErrors?.[0].message || "";
  const disabled = !!(loading || isDowngradeScheduled || isPaused);

  const handleCancel = (e) => {
    openModal(e, "CancelBoostSubscriptionModal", { email });
  };

  const handleRestore = () => {
    undoCancelBoostSubscription();
  };

  const tips = [];
  if (isDowngradeScheduled) {
    tips.push(
      "the subscription cannot be canceled if a downgrade is scheduled",
    );
  }
  if (isPaused) {
    tips.push("the subscription cannot be canceled if payments are paused");
  }

  const pastDueNote = isPastDue
    ? "cancel immediately"
    : "cancel at end of billing cycle";
  const buttonNote = isCanceled
    ? "resume at start of billing cycle"
    : pastDueNote;

  return (
    <div className="flex flex-col w-full">
      <Detail scheduledCancellationDate={scheduledCancellationDate} />
      {!!tips.length &&
        tips.map((tip) => (
          <p className="mt-2 text-sm italic text-gray-700">{tip}</p>
        ))}
      {isCanceled ? (
        <RestoreButton
          disabled={disabled}
          loading={loading}
          handleRestore={handleRestore}
        />
      ) : (
        <CancelButton disabled={disabled} handleCancel={handleCancel} />
      )}
      <p className="text-sm self-end text-gray-700">{buttonNote}</p>
      <p className="mt-1 text-red-500">{errorMessage}</p>
    </div>
  );
};

export default BoostSubscriptionCancel;
